import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { IReceptionSubscription } from "../../../../core/api/responsecenters/types";
import { Row, Spacer } from "../../../../components/Layout/Layout";
import { removeARCSubscription } from "../../../../core/api/responsecenters/responsecenters";

import Table from "../../../../ui-lib/components/Tables/Table";
import TableCell from "../../../../ui-lib/components/Tables/TableCell";
import Button from "../../../../ui-lib/components/Button/Button";
import CreateSubscriptionModal from "./CreateSubscriptionModal";
import useUser from "../../../../core/user/useUser";
import DeleteModal from "../../../../components/DeleteModal/DeleteModal";
import AlarmSubscriptionDetails from "./AlarmSubscriptionDetails/AlarmSubscriptionDetails";
import { EditSubscriptionActions } from "./AlarmSubscriptionDetails/SubscriptionEditModal";
import NoItems from "../../../../ui-lib/components/Tables/NoItems";

const AlarmSubscriptionsTable = ({
  rcId,
  subscriptions,
  sellers,
  getAllArcSubscriptions,
}: {
  rcId: number;
  sellers: any;
  subscriptions?: IReceptionSubscription[];
  getAllArcSubscriptions: () => Promise<void>;
}) => {
  const { t } = useTranslation();
  const { authenticatedRequest } = useUser();

  const subscriptionRef = useRef<IReceptionSubscription>();
  const subscriptionEditModalRef = useRef<EditSubscriptionActions>(null);

  const [isAlarmSubscriptionOpen, setIsAlarmSubscriptionOpen] = useState(false);
  const [isAddSubscriptionOpen, setIsAddSubscriptionOpen] = useState(false);
  const [subscriptionDeleteModalOpen, setSubscriptionDeleteModalOpen] =
    useState(false);

  return (
    <>
      <Row type="space" className="w-100">
        <h2>{t("AlarmReceptions:subscription_list")}</h2>
        <Button
          size="small"
          variant="secondary"
          text={t("AlarmReceptions:add_new_subscription")}
          onClick={() => setIsAddSubscriptionOpen(true)}
        />
      </Row>
      <Spacer size={16} />
      {subscriptions?.length ? (
        <Table<IReceptionSubscription>
          showRowHover
          hideEmptyMessage
          items={subscriptions}
          columns={[
            {
              key: "name",
              header: t("Common:name"),
              fieldTemplate: (rowData) => (
                <TableCell
                  onClick={() => {
                    subscriptionRef.current = rowData;
                    setIsAlarmSubscriptionOpen(true);
                  }}
                  value={rowData.description}
                />
              ),
              style: {
                minWidth: "100px",
              },
            },
            {
              key: "resellers",
              header: t("Common:sellers"),
              fieldTemplate: ({ sellerAccess }) => {
                const outerSeller = sellerAccess
                  .map((seller) => seller.name)
                  .join(", ");
                return <TableCell value={outerSeller} />;
              },
            },
            {
              key: "organisations",
              header: t("Organizations:organizations"),
              fieldTemplate: ({ organizationAccess }) => {
                const orgs = organizationAccess
                  .map((org) => org.name)
                  .join(", ");
                return <TableCell value={orgs} />;
              },
            },
          ]}
          rowActions={[
            {
              text: t("Common:label_edit"),
              icon: "pencil-alt",
              onClick: (rowData) => {
                subscriptionRef.current = rowData;
                setIsAlarmSubscriptionOpen(true);
              },
            },
            {
              text: t("Common:delete"),
              icon: "trash",
              iconVariant: "secondary",
              onClick: (rowData) => {
                subscriptionRef.current = rowData;
                setSubscriptionDeleteModalOpen(true);
              },
            },
          ]}
          rowActionsColWidth={45}
        />
      ) : (
        <NoItems
          title={t("Table:noresult_title")}
          icon="eye-off"
          subTitle={t("Table:noresult_subtitle")}
          clear={undefined}
          style={{ margin: "auto" }}
        />
      )}
      {isAddSubscriptionOpen && (
        <CreateSubscriptionModal
          isOpen={isAddSubscriptionOpen}
          arcId={rcId}
          onClose={() => setIsAddSubscriptionOpen(false)}
          onSibmit={getAllArcSubscriptions}
        />
      )}
      {subscriptionDeleteModalOpen && (
        <DeleteModal
          isOpen={subscriptionDeleteModalOpen}
          title={t("AlarmReceptions:delete_subscription")}
          locizeKey="arc_subscription"
          replaceKey={subscriptionRef.current?.description}
          callDelete={() =>
            removeARCSubscription(
              authenticatedRequest,
              subscriptionRef.current!.subscriptionGroupId,
              rcId,
            )
          }
          onDelete={getAllArcSubscriptions}
          onClose={() => {
            setSubscriptionDeleteModalOpen(false);
          }}
        />
      )}

      {isAlarmSubscriptionOpen && (
        <AlarmSubscriptionDetails
          arcId={rcId}
          sellers={sellers}
          authenticatedRequest={authenticatedRequest}
          isAlarmSubscriptionOpen={isAlarmSubscriptionOpen}
          subscription={subscriptionRef}
          setIsAlarmSubscriptionOpen={setIsAlarmSubscriptionOpen}
          subscriptionEditModalRef={subscriptionEditModalRef}
          getAllArcSubscriptions={getAllArcSubscriptions}
        />
      )}
    </>
  );
};

export default AlarmSubscriptionsTable;
